import {Inject, Injectable, DOCUMENT} from '@angular/core';
import {environment} from '@environments/environment';
import {CssOverwrites} from '../../models/css-overwrites.model';
import {privatizePartnerFontFaces, rewriteCustomCssForDocumentOverlay, rewriteCustomCssForShadowRoot} from './custom-css.util';
import {LoggerService} from './logger.service';
import {SMART_WIDGET_OVERLAY_CONTAINER_CLASS} from '../../smart-widget-shadow-host/shadow-root-overlay-container';
import {LightDomProjectionDirective} from '../../directives/light-dom-projection.directive';

@Injectable({
  providedIn: 'root'
})
export class DomManipulationService {

  /** WC-mode shadow host; undefined in iframe mode, where document-level styles already reach. */
  private shadowHost?: HTMLElement;

  /** Buttons hidden via the `button_*_step_*` context flags, replayed if the shadow root arrives later. */
  private readonly hiddenButtonIds = new Set<string>();

  /** The partner stylesheet currently adopted by the shadow root, so a re-apply replaces it. */
  private previousAdoptedSheet?: CSSStyleSheet;

  private previousShadowStyle?: HTMLStyleElement;

  constructor(
    @Inject(DOCUMENT) private document: Document,
  ) { }

  /** Called by SmartWidgetShadowHostComponent on destroy so a detached root stops receiving styles. */
  unregisterShadowHost(host: HTMLElement): void {
    if (this.shadowHost === host) {
      this.shadowHost = undefined;
      this.previousAdoptedSheet = undefined;
      this.previousShadowStyle = undefined;
    }
  }

  registerShadowHost(host: HTMLElement): void {
    this.shadowHost = host;
    // Replay flags collected before the root existed — they would otherwise be lost.
    this.hiddenButtonIds.forEach(id => this.appendHideButtonStyle(this.shadowRoot(), id));
  }

  setCustomNextPreviewButtonStyle(id: string): void {
    this.hiddenButtonIds.add(id);
    // The button renders INSIDE the shadow root, where a document-level rule cannot reach it - and
    // a document-level `#id { display: none }` is a global rule on the integrator's page, so it is
    // only written where it is the one that works: the light DOM (iframe delivery, `light-dom` embed).
    const shadowRoot = this.shadowRoot();
    if (shadowRoot) {
      this.appendHideButtonStyle(shadowRoot, id);
    } else {
      this.appendHideButtonStyle(this.document.head, id);
    }
  }

  applyCustomCss(css: CssOverwrites): void {
    const shadowRoot = this.shadowRoot();
    const style = this.document.createElement('style');
    style.setAttribute('scoped', '');

    if (!shadowRoot || !this.shadowHost) {
      // Light DOM (iframe delivery, or a Web Component embed that opted out with `light-dom`): the
      // document copy is the whole story, exactly as it always was.
      style.textContent = css.code;
      this.document.body.append(style);
      return;
    }

    // Shadow DOM. The document still needs a copy — `@font-face` / `@import` only load from the
    // document, and the CDK overlays (datepicker, dialogs) end up on `document.body` whenever
    // ShadowRootOverlayContainer has to leave them there — but appended raw it restyles the
    // integrator's page: a partner sheet's `body { … }`, `* { … }`, `a { … }`, `.btn { … }` reach
    // every element on it. So every rule is scoped under the light-DOM nodes the widget OWNS — its
    // overlay container and the slot hosts the payment mounts are projected into (a partner styling
    // its payment box must keep reaching it; the shadow copy cannot, slotted descendants are out of
    // its reach). At-rules pass through; no ordinary style rule can reach the page. Deliberate
    // authoring still can (`@import`, a nested `&` inside `:not()`/`:has()`/`:is()`/`:where()`) - the sheet is the
    // partner's own, for the partner's own page, so that is their choice, not a leak.
    //
    // The one thing that copy carries verbatim — `@font-face` — is also a reach: a face declared under
    // a family the page uses joins that family and, declared last, wins its descriptor match for the
    // page's own text. So the families the sheet declares are first made private to the widget
    // (`"X"` → `"X cbsw"`, every reference listing the private name first); the widget still renders
    // the same files, the page's families gain nothing.
    let partnerCss = css.code;
    try {
      partnerCss = privatizePartnerFontFaces(css.code);
    } catch (error) {
      LoggerService.warn('[css-overwrite] Could not privatize the partner @font-face families; keeping the family names', error);
    }
    let documentCss = partnerCss;
    try {
      documentCss = rewriteCustomCssForDocumentOverlay(partnerCss, environment.smartWidgetElementTag,
        [SMART_WIDGET_OVERLAY_CONTAINER_CLASS, LightDomProjectionDirective.SLOT_CLASS]);
    } catch (error) {
      // A sheet the rewriter cannot parse is not allowed onto the page raw — the shadow copy below
      // still styles the widget itself; only declined-overlay styling and fonts are lost.
      LoggerService.warn('[css-overwrite] Could not scope the partner CSS for the document; skipping the document copy', error);
      documentCss = '';
    }
    if (documentCss) {
      style.textContent = documentCss;
      this.document.body.append(style);
    }

    // Also inside the shadow tree, where selectors anchored above it (`:root`, `html`, `body`, the
    // element itself) never match — rewritten to the host with specificity preserved.
    const host = this.shadowHost;
    // `ancestor`: page wrappers above the host. `parent`: the widget ELEMENT's direct parent - a partner's
    // `body > calenso-smart-widget` must stay as dead on a nested embed as it was in the light DOM.
    const element = host.closest(environment.smartWidgetElementTag) ?? host;
    const hostAncestorMatches = (compound: string, relation: 'ancestor' | 'parent' = 'ancestor') =>
      relation === 'parent' ? (element.parentElement?.matches(compound) ?? false) : host.closest(compound) !== null;
    let shadowCss = partnerCss;
    try {
      shadowCss = rewriteCustomCssForShadowRoot(partnerCss, environment.smartWidgetElementTag, hostAncestorMatches);
    } catch (error) {
      // A malformed sheet must not take the bootstrap down; adopt as-is (loses document-anchored rules).
      LoggerService.warn('[css-overwrite] Could not rewrite the partner CSS for the shadow root, adopting it as-is', error);
    }
    this.applyCustomCssToShadowRoot(shadowRoot, shadowCss);
  }

  private shadowRoot(): ShadowRoot | null {
    return this.shadowHost?.shadowRoot ?? null;
  }

  private appendHideButtonStyle(root: Node | null, id: string): void {
    if (!root) {
      return;
    }
    const style = this.document.createElement('style');
    style.textContent = `#${id} { display: none; }`;
    root.appendChild(style);
  }

  /**
   * A constructed sheet cascades after every `<style>` in the tree, so the partner CSS keeps the
   * "always last" position it has in the light DOM.
   */
  private applyCustomCssToShadowRoot(shadowRoot: ShadowRoot, cssText: string): void {
    // Drop the previous application whichever path it took: the two can alternate, and a crossing
    // that clears only its own path leaves the old rules applying alongside the new.
    this.removePreviousShadowCss(shadowRoot);

    // `replaceSync()` DROPS `@import` silently (no throw), so those sheets take the <style> path;
    // the only cost is cascade position. Word boundary, not `\s` — minified CSS writes
    // `@import"x.css"`; a false positive costs nothing but position, so err wide.
    const importsRules = /@import\b/i.test(cssText);
    if (!importsRules && 'adoptedStyleSheets' in shadowRoot && typeof CSSStyleSheet !== 'undefined' && 'replaceSync' in CSSStyleSheet.prototype) {
      try {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(cssText);
        shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
        this.previousAdoptedSheet = sheet;
        return;
      } catch (error) {
        LoggerService.warn('[css-overwrite] Constructed stylesheet rejected, falling back to a <style> element', error);
      }
    }
    const style = this.document.createElement('style');
    style.setAttribute('scoped', '');
    style.textContent = cssText;
    shadowRoot.append(style);
    this.previousShadowStyle = style;
  }

  /** Removes the partner CSS this service last applied to `shadowRoot`, by either path. */
  private removePreviousShadowCss(shadowRoot: ShadowRoot): void {
    if (this.previousAdoptedSheet) {
      const stale = this.previousAdoptedSheet;
      shadowRoot.adoptedStyleSheets = shadowRoot.adoptedStyleSheets.filter(s => s !== stale);
      this.previousAdoptedSheet = undefined;
    }
    this.previousShadowStyle?.remove();
    this.previousShadowStyle = undefined;
  }
}
